import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Banknote, CreditCard, QrCode, Building2, Wallet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// FastAPI errors can be a string, a {message,...} object, or a validation array.
const extractDetail = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  if (typeof detail === 'object') return detail.message || JSON.stringify(detail);
  return fallback;
};

const METHODS = [
  { key: 'cash', label: 'Cash', icon: Banknote },
  { key: 'card', label: 'Card', icon: CreditCard },
  { key: 'qr_gcash', label: 'GCash', icon: QrCode },
  { key: 'qr_maya', label: 'Maya', icon: QrCode },
  { key: 'bank', label: 'Bank', icon: Building2 },
  { key: 'store_credit', label: 'Store Credit', icon: Wallet },
];

export default function PaymentModal({ open, onClose, total, cart, outletId, tableId, shift, onComplete, currentTicket }) {
  const { token } = useAuth();
  const [ticketId, setTicketId] = useState(currentTicket?.id || null);
  const [paidSoFar, setPaidSoFar] = useState(0);
  const [tenders, setTenders] = useState([]);
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState('');       // this tender's amount; blank = full remaining
  const [tendered, setTendered] = useState('');
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  // Senior Citizen / PWD / Government tax exemption
  const [scType, setScType] = useState('');
  const [scId, setScId] = useState('');
  const [scName, setScName] = useState('');
  const [liveTotal, setLiveTotal] = useState(null); // post-exemption total from backend

  // Reset internal state each time the modal is (re)opened so a previous
  // ticket's tenders never carry over into the next sale.
  React.useEffect(() => {
    if (open) {
      setTicketId(currentTicket?.id || null);
      setPaidSoFar(0);
      setTenders([]);
      setMethod('cash');
      setAmount(''); setTendered(''); setReference('');
      setScType(''); setScId(''); setScName(''); setLiveTotal(null);
    }
  }, [open, currentTicket]);

  const effectiveTotal = liveTotal != null ? liveTotal : total;
  const remaining = round2(Math.max(0, effectiveTotal - paidSoFar));
  const thisAmount = amount === '' ? remaining : round2(parseFloat(amount) || 0);
  const isSplit = thisAmount > 0 && thisAmount < remaining - 0.005;
  const change = method === 'cash' && tendered ? Math.max(0, round2(parseFloat(tendered) - thisAmount)) : 0;

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const resetTenderInputs = () => { setAmount(''); setTendered(''); setReference(''); };

  // Create the ticket lazily (quick-service) or return the existing one
  const ensureTicket = async () => {
    if (ticketId) return ticketId;
    const createRes = await fetch(`${API_URL}/api/pos/tickets`, {
      method: 'POST', headers: authHeaders,
      body: JSON.stringify({
        outlet_id: outletId, table_id: tableId, guest_count: 1,
        items: cart.map(item => ({ menu_item_id: item.id, quantity: item.quantity, notes: item.notes })),
      }),
    });
    const ticketData = await createRes.json();
    if (!createRes.ok) throw new Error(extractDetail(ticketData.detail, 'Failed to create ticket'));
    setTicketId(ticketData.id);
    return ticketData.id;
  };

  const applyScPwd = async (type) => {
    if ((type === 'senior' || type === 'pwd') && !scId.trim()) { toast.error('Enter the ID number'); return; }
    setLoading(true);
    try {
      const tid = await ensureTicket();
      const res = await fetch(`${API_URL}/api/pos/tickets/${tid}/sc-pwd`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ type: type || 'none', id_number: scId || null, name: scName || null }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(extractDetail(d.detail, 'Failed to apply exemption'));
      setLiveTotal(round2(d.total));
      toast.success(type ? `${type.toUpperCase()} exemption applied` : 'Exemption removed');
    } catch (err) { toast.error(err.message || 'Failed'); }
    setLoading(false);
  };

  const handlePayment = async () => {
    if (thisAmount <= 0) { toast.error('Enter a payment amount'); return; }
    if (thisAmount > remaining + 0.005) { toast.error('Amount exceeds balance due'); return; }
    if (method === 'cash' && (!tendered || parseFloat(tendered) < thisAmount)) {
      toast.error('Insufficient amount tendered');
      return;
    }
    if ((method === 'qr_gcash' || method === 'qr_maya' || method === 'bank') && !reference) {
      toast.error('Reference number required for this method');
      return;
    }

    setLoading(true);
    try {
      const tid = await ensureTicket();

      const payRes = await fetch(`${API_URL}/api/pos/tickets/${tid}/pay`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          method,
          amount: thisAmount,
          tendered: method === 'cash' ? parseFloat(tendered) : null,
          reference_number: reference || null,
        }),
      });
      const payData = await payRes.json();
      if (!payRes.ok) throw new Error(extractDetail(payData.detail, 'Payment failed'));

      if (method === 'cash' && payData.change > 0) toast.success(`Change: ₱${payData.change.toFixed(2)}`);

      const settled = Math.min(thisAmount, remaining);
      const newPaid = round2(paidSoFar + settled);
      setTenders(prev => [...prev, { method, amount: settled }]);

      if (payData.ticket_status === 'paid' || newPaid >= total - 0.005) {
        toast.success('Payment complete');
        onComplete(tid);  // pass the paid ticket id so the parent can offer to print the receipt
        onClose();  // close ourselves; don't rely on the parent toggling `open`
      } else {
        setPaidSoFar(newPaid);
        resetTenderInputs();
        toast.info(`Partial payment recorded. Remaining ₱${round2(total - newPaid).toFixed(2)}`);
      }
    } catch (err) {
      toast.error(err.message || 'Payment failed');
    }
    setLoading(false);
  };

  const methodLabel = (k) => METHODS.find(m => m.key === k)?.label || k;
  const quickAmounts = [100, 200, 500, 1000];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between flex-wrap gap-2">
            Payment
            <span className="text-2xl text-secondary">₱{remaining.toFixed(2)}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {tenders.length > 0 && (
            <div className="rounded-lg bg-muted p-3 text-sm space-y-1" data-testid="tender-list">
              <p className="text-muted-foreground font-medium">Tenders</p>
              {tenders.map((t, i) => (
                <div key={i} className="flex justify-between">
                  <span>{methodLabel(t.method)}</span>
                  <span>₱{t.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-border pt-1 mt-1 font-semibold">
                <span>Balance due</span><span>₱{remaining.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Senior Citizen / PWD / Government tax exemption (BIR) */}
          <div className="rounded-lg border border-border p-3">
            <label className="text-xs text-muted-foreground">Tax exemption (Senior Citizen / PWD / Government)</label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              <select value={scType}
                onChange={(e) => { const v = e.target.value; setScType(v); if (!v) applyScPwd(''); }}
                className="col-span-1 h-9 px-2 rounded-md bg-muted border border-border text-sm text-foreground" data-testid="scpwd-type">
                <option value="">Regular</option>
                <option value="senior">Senior</option>
                <option value="pwd">PWD</option>
                <option value="government">Gov't</option>
              </select>
              <Input value={scId} onChange={(e) => setScId(e.target.value)} placeholder="ID number"
                className="col-span-1 h-9 bg-muted border-border text-sm" data-testid="scpwd-id" />
              <Input value={scName} onChange={(e) => setScName(e.target.value)} placeholder="Name"
                className="col-span-1 h-9 bg-muted border-border text-sm" />
              <Button type="button" variant="outline" className="col-span-1 h-9"
                disabled={loading || !scType} onClick={() => applyScPwd(scType)} data-testid="scpwd-apply">Apply</Button>
            </div>
            {liveTotal != null && scType && (
              <p className="text-xs text-secondary mt-1">Exemption applied — VAT removed{['senior','pwd'].includes(scType) ? ' + 20% discount' : ''}. New total ₱{effectiveTotal.toFixed(2)}.</p>
            )}
          </div>

          {/* Payment Method */}
          <div className="grid grid-cols-3 gap-2">
            {METHODS.map(({ key, label, icon: Icon }) => (
              <Button key={key} variant={method === key ? 'default' : 'outline'} onClick={() => setMethod(key)}
                      className={method === key ? 'bg-secondary' : ''} data-testid={`pay-${key}-btn`}>
                <Icon className="w-4 h-4 mr-1.5" /> {label}
              </Button>
            ))}
          </div>

          {/* Amount for this tender (enables split) */}
          <div>
            <label className="text-sm text-muted-foreground flex items-center gap-2">
              <Wallet className="w-3.5 h-3.5" /> Amount for this payment
              {isSplit && <span className="text-xs text-amber-600">(split — balance will remain)</span>}
            </label>
            <Input type="number" value={amount} onFocus={(e) => e.target.select()}
                   onChange={e => setAmount(e.target.value)} placeholder={remaining.toFixed(2)}
                   className="bg-muted border-border text-lg" data-testid="pay-amount-input" />
            <div className="flex gap-2 mt-1">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setAmount(remaining.toFixed(2))}>Full ₱{remaining.toFixed(2)}</Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setAmount((remaining / 2).toFixed(2))}>Half</Button>
            </div>
          </div>

          {method === 'cash' && (
            <>
              <div>
                <label className="text-sm text-muted-foreground">Amount Tendered</label>
                <Input type="number" value={tendered} onFocus={(e) => e.target.select()}
                       onChange={e => setTendered(e.target.value)} placeholder="Enter amount"
                       className="bg-muted border-border text-lg" data-testid="tendered-input" />
              </div>
              <div className="flex flex-wrap gap-2">
                {quickAmounts.map(amt => (
                  <Button key={amt} size="sm" variant="outline" onClick={() => setTendered(amt.toString())}
                          className="flex-1 min-w-[60px]">₱{amt}</Button>
                ))}
                <Button size="sm" variant="outline" onClick={() => setTendered(thisAmount.toFixed(2))}
                        className="flex-1 min-w-[60px] bg-muted">Exact</Button>
              </div>
              {tendered && parseFloat(tendered) >= thisAmount && (
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <span className="text-muted-foreground">Change: </span>
                  <span className="text-2xl font-bold text-green-600">₱{change.toFixed(2)}</span>
                </div>
              )}
            </>
          )}

          {(method === 'qr_gcash' || method === 'qr_maya' || method === 'card' || method === 'bank') && (
            <div>
              <label className="text-sm text-muted-foreground">Reference Number{method === 'card' ? ' (optional)' : ''}</label>
              <Input value={reference} onChange={e => setReference(e.target.value)}
                     placeholder="Enter reference number" className="bg-muted border-border"
                     data-testid="reference-input" />
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>{tenders.length > 0 ? 'Close' : 'Cancel'}</Button>
          <Button onClick={handlePayment} disabled={loading} className="bg-secondary hover:bg-secondary/90"
                  data-testid="confirm-payment-btn">
            {loading ? 'Processing...' : (isSplit ? `Pay ₱${thisAmount.toFixed(2)}` : 'Confirm Payment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
